import { css } from '@emotion/core'
import loadable from '@loadable/component'
import React from 'react'
import { CSSTransition } from 'react-transition-group'

import { headerBreakPoint } from '../constants/breakPoints'
import { breakpoints } from '../constants/breakPoints'
import { HEADER_HEIGHT } from '../constants/header'
import { isMobile } from '../helpers/common'
import { groupsFactory, translate } from '../helpers/header'
import useDebouncedCallback from '../hooks/useDebouncedCallback'
import { useLanguage } from '../hooks/useLanguage'
import { useOnClickOutside } from '../hooks/useOnClickOutside'
import { useWindowResize } from '../hooks/useWindowResize'
import Chevron from '../icons/abstract/chevron.svg'
import { DropdownEntry, Entry, HeaderMenuItem, HeaderProps } from '../interfaces/header'
import { useTranslation } from '../lang'
import HeaderNavigation from './HeaderNavigation'
import Image from './Image'
import { Link } from './Link'
import { LoadingSpinner } from './LoadingComponent'
import SearchBar, { searchBarHeight } from './Search'
import VerticalTextDivider from './VerticalTextDivider'

const MegaMenuDropdown = loadable(() => import('./MegaMenuDropdown'))
const HeaderMobile = loadable(() => import('./HeaderMobile'))

const Header: React.FunctionComponent<HeaderProps> = ({
  distractionFreeMode,
  logoTitle = '',
  hideDemoButtonOnMobile,
  withNavigation = true,
  titleHref,
  entriesLogin,
  onlyLogoMode = false,
  entriesNavigation,
  styles,
  isDark = false,
  isSticky = true,
  isMobileSticky = true,
  withDivider = false,
  hideLogoTitleOnDesktop = false,
  hideOnScroll = false,
  customBreakPoint = headerBreakPoint,
  withSearchOnSmallOnly = false,
  search = '',
  setSearch = () => {},
  searchPlaceholder,
}) => {
  const headerRef = React.useRef<HTMLDivElement>(null)
  const dropdownRef = React.useRef<HTMLDivElement>(null)

  const [mobileOpen, setMobileOpenState] = React.useState(false)
  const [scrolled, setScrolled] = React.useState(false)
  const windowSize = useWindowResize()

  // hiding Header on scrolling down (depending on property)
  const [scrollingDown, setScrollingDown] = React.useState(false)
  const lastScrollingPosition = React.useRef<number>(0)

  const setMobileOpen = (isOpen: boolean) => {
    document.body.style.overflowY = isOpen ? 'hidden' : 'auto'
    setMobileOpenState(isOpen)
  }

  const [dropdown, setDropdown] = React.useState<{ dropdownItems: HeaderMenuItem[] | undefined; name: string }>()
  const language = useLanguage()
  const groups = React.useMemo(
    () => groupsFactory(language, entriesLogin, entriesNavigation, customBreakPoint),
    [language, windowSize, customBreakPoint]
  )

  // dropdown related
  const [isDropdownOpen, _setIsDropdownOpen] = React.useState(false)
  // debounce used so the dropdown closes properly when clicking twice on the resources button
  const [setIsDropdownOpen] = useDebouncedCallback(_setIsDropdownOpen, 150)
  useOnClickOutside(dropdownRef, () => setIsDropdownOpen(false))

  const headerDistanceToTopOfScreen = React.useMemo(
    () => distanceToTop(headerRef.current),
    [headerRef.current, isDropdownOpen]
  )
  const maxGroupBreakPoint = groups.reduce((n, g) => Math.max(n, g.breakPoint), 0)

  const { t } = useTranslation()

  React.useEffect(() => {
    document.body.style.overflowY = isDropdownOpen ? 'hidden' : 'auto'
    return () => {
      document.body.style.overflowY = 'auto'
    }
  }, [isDropdownOpen])

  React.useEffect(() => {
    let throttleTimer = false
    const throttleTime = 100 // Run the handler at most every 100ms

    const scrollHandler = () => {
      // If a timer is running, ignore this scroll event
      if (throttleTimer) {
        return
      }

      // Set a timer so the next events are ignored
      throttleTimer = true

      setTimeout(() => {
        // The actual logic you want to run
        const stashingElements = document.getElementsByClassName('stashing-block') as HTMLCollectionOf<HTMLElement>
        const stashingBlockInView =
          stashingElements[0]?.getBoundingClientRect().top < 0.8 * window.innerHeight &&
          stashingElements[stashingElements.length - 1]?.getBoundingClientRect().bottom > 100

        setScrolled(window.scrollY > 0)

        if (window.scrollY > 0) {
          setScrollingDown(stashingBlockInView ? window.scrollY > lastScrollingPosition.current : false)
          lastScrollingPosition.current = window.scrollY
        }

        // After 100ms, release the timer to allow the handler to run again
        throttleTimer = false
      }, throttleTime)
    }

    document.addEventListener('scroll', scrollHandler)

    return () => {
      document.removeEventListener('scroll', scrollHandler)
    }
  }, [])

  const onClick = (entry: Entry) => (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
    setMobileOpen(false)
    const hashMatch = translate(entry.url || '').match(/#(.+)$/)
    if (hashMatch && entry.preferHashOnCurrentPage && document.querySelector('#' + hashMatch[1])) {
      event.preventDefault()
      window.location.hash = hashMatch[1]
    }
  }

  const onDropdownClick = (entry: DropdownEntry) => () => {
    if (translate(entry.label) === dropdown?.name && isDropdownOpen) {
      setIsDropdownOpen(false)
    } else {
      setIsDropdownOpen(true)
      setDropdown({ dropdownItems: entry.dropdownItems, name: translate(entry.label) })
    }
  }

  const getChevronIcon = (entry: Entry) => {
    return (
      <span>
        {entry.type === 'dropdown' && (
          <Chevron
            css={chevronStyle(isDark, translate(entry.label) === dropdown?.name && isDropdownOpen, customBreakPoint)}
          />
        )}
      </span>
    )
  }

  const logoSrc = isDark
    ? 'airfocus-logos/airfocus-lucid-logo-white.svg'
    : // winter logo is already in images folder: "airfocus-logos/airfocus-logo-winter.svg"
      'airfocus-logos/airfocus-lucid-logo.svg'

  return (
    <React.Fragment>
      <header
        id={'main-header'}
        className={isSticky ? 'full-screen-sticky' : 'full-screen-relative'}
        ref={headerRef}
        css={[
          containerStyle(
            scrolled,
            hideOnScroll,
            scrollingDown,
            isDark,
            isSticky,
            isMobileSticky,
            withNavigation,
            customBreakPoint,
            distractionFreeMode
          ),
          styles?.containerStyle?.(scrolled),
        ]}
      >
        <div css={airfocusLogoStyle}>
          <Link href={t('homepageLink')} onClick={() => setMobileOpen(false)} aria-label={'Home'}>
            <Image src={logoSrc} alt={'airfocus-logo'} css={logoStyle(isDark, maxGroupBreakPoint)} loading={'eager'} />
          </Link>

          {withDivider && (
            <VerticalTextDivider
              className={hideLogoTitleOnDesktop ? 'hide-on-large-desktop' : undefined}
              height={'24px'}
              color={isDark ? '#808080' : '#475267'}
              marginLeftRight={'0'}
              translateY={'0'}
            />
          )}

          {logoTitle && (
            <Link
              href={titleHref}
              css={logoTitleStyle(isDark)}
              className={`strong-body-1 ${hideLogoTitleOnDesktop && 'hide-on-large-desktop'}`}
            >
              {logoTitle}
            </Link>
          )}
        </div>
        {!onlyLogoMode && (
          <HeaderNavigation
            distractionFreeMode={distractionFreeMode}
            withNavigation={withNavigation}
            isDark={isDark}
            styles={styles}
            onClick={onClick}
            groups={groups}
            isDropdownOpen={isDropdownOpen}
            onDropdownClick={onDropdownClick}
            dropdown={dropdown}
            getChevronIcon={getChevronIcon}
            search={search}
            setSearch={setSearch}
            searchPlaceholder={searchPlaceholder}
          />
        )}
        {isMobile(customBreakPoint) && (
          <HeaderMobile
            distractionFreeMode={distractionFreeMode}
            hideDemoButtonOnMobile={hideDemoButtonOnMobile}
            withNavigation={withNavigation}
            isDark={isDark}
            maxGroupBreakPoint={maxGroupBreakPoint}
            onClick={onClick}
            mobileOpen={mobileOpen}
            setMobileOpen={setMobileOpen}
            groups={groups}
            isDropdownOpen={isDropdownOpen}
            onDropdownClick={onDropdownClick}
            dropdown={dropdown}
            getChevronIcon={getChevronIcon}
            withSearchOnSmallOnly={withSearchOnSmallOnly}
            search={search}
            setSearch={setSearch}
            searchPlaceholder={searchPlaceholder}
          />
        )}
      </header>
      {(groups.filter((g) => g.entries.find((e) => e.type === 'search')).length > 0 ||
        (withSearchOnSmallOnly && isMobile(breakpoints.small))) && (
        <>
          {isMobile(breakpoints.small) && (
            <SearchBar
              search={search}
              blackBG={isDark}
              setSearch={setSearch}
              placeholder={searchPlaceholder}
              hideOnScrollAndScrollingDown={hideOnScroll && scrollingDown}
            />
          )}
          {search && <LoadingSpinner blackBG={isDark} />}
        </>
      )}
      <CSSTransition
        in={isDropdownOpen && dropdown?.dropdownItems && !isMobile()}
        timeout={1000}
        css={dropdownAnimation}
        className={'fade'}
        unmountOnExit
      >
        {() =>
          dropdown?.dropdownItems &&
          !isMobile(customBreakPoint) && (
            <div
              ref={dropdownRef}
              className={'full-screen-fixed'}
              css={[
                dropdownContainerStyle(headerDistanceToTopOfScreen, scrolled, customBreakPoint, isDark),
                dropdownAnimation,
              ]}
            >
              <MegaMenuDropdown
                customBreakPoint={customBreakPoint}
                isDark={isDark}
                megaMenuItems={dropdown?.dropdownItems ?? []}
              />
            </div>
          )
        }
      </CSSTransition>
    </React.Fragment>
  )
}

export default Header

const containerStyle = (
  scrolled: boolean,
  hideOnScroll: boolean,
  scrollingDown: boolean,
  isDark: boolean,
  isSticky: boolean,
  isMobileSticky: boolean,
  withNavigation: boolean,
  customBreakPoint: number,
  distractionFreeMode?: boolean
) => css`
  position: ${isSticky ? 'sticky' : 'relative'};
  z-index: 7;
  top: 0;
  left: 0;
  width: 100%;
  background-color: ${isDark ? 'rgba(7, 19, 34, 0.8)' : scrolled ? 'rgba(255, 255, 255, 0.8)' : '#fff'};
  backdrop-filter: blur(20px);
  ${!isDark && 'transition: all 300ms ease-in-out;'};
  max-height: 4.5rem;
  padding: 1rem 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: solid 1px ${isDark ? 'rgba(255, 255, 255, 0.2)' : '#efeff0'};
  transition:
    transform 0.75s ease-in-out,
    opacity 0.75s ease-in;

  // hide Header on scrolling down
  // translateY(-200%) to establish slight time delay for fade-in
  ${hideOnScroll &&
  scrollingDown &&
  `
    transform: translateY(-${HEADER_HEIGHT + searchBarHeight}px);
    opacity: 0;
  `};

  @media screen and (max-width: ${headerBreakPoint}px) {
    height: 3.5rem;
  }

  @media screen and (max-width: ${customBreakPoint}px) {
    min-width: 100vw;
    box-sizing: border-box;
    ${!isMobileSticky && 'position: relative !important'};
  }

  @media screen and (min-width: ${customBreakPoint + 1}px) {
    display: grid;
    ${!distractionFreeMode && 'grid-gap: 2rem;'};
    grid-template-columns: ${withNavigation ? 'auto 1fr auto' : 'auto 1fr'};
  }
`

export const entryLinkStyle = (isDark: boolean, isHighlighted?: boolean, isMobile?: boolean) => css`
  color: ${isDark ? '#fff' : '#0A1C33'};
  fill: ${isDark ? '#fff' : '#0A1C33'};
  stroke: ${isDark ? '#fff' : '#0A1C33'};
  ${isHighlighted &&
  (isDark
    ? 'color: #fff'
    : `
      // copy
        color: #318eff;

      // chevron
        svg > g > path {
          stroke: #318eff;
        }`)};
  text-decoration: none;

  ${isMobile &&
  `
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    ${isHighlighted && 'padding-bottom: 0;'}

  `}

  .footnote {
    color: white !important;
  }

  &:hover {
    // copy
    color: #318eff;

    // chevron
    svg > g > path {
      stroke: #318eff;
    }
  }
`

const airfocusLogoStyle = css`
  display: flex;
  align-items: center;
  text-decoration: none !important;

  a {
    display: flex;
  }
`

const logoStyle = (isDark: boolean, maxGroupBreakPoint: number) => css`
  // uncomment next line for airfocus-logo-winter icon only (only for bright design)
  /* ${!isDark && 'transform: translateY(-2px); margin-right: 0.25rem !important;'}; */
  width: 7rem;
  height: 2rem;
  max-height: 2rem;
  margin-right: 0.75rem;

  @media screen and (max-width: ${maxGroupBreakPoint}px) {
    width: 6rem;
    max-height: 1.75rem;
    padding-left: 0;
  }

  @media screen and (max-width: 340px) {
    width: 6.5rem;
  }
`

const logoTitleStyle = (isDark: boolean) => css`
  position: relative;
  color: ${isDark ? '#fff' : '#475267'};
  padding-left: 0.75rem;

  &:hover {
    color: #318eff;
  }
`

const dropdownAnimation = css`
  transition: all 400ms cubic-bezier(0.22, 1, 0.36, 1);
  &.enter {
    opacity: 0;
    transform: translateY(-80px);
    > div > div > div > * {
      opacity: 0;
      transform: translateY(-15px);
    }
  }
  &.enter-active {
    opacity: 1;
    transform: translateY(0);
    > div > div {
      > div > * {
        opacity: 1;
        transform: translateY(0);
        transition:
          opacity 450ms cubic-bezier(0, 0, 0.16, 0.96),
          transform 400ms cubic-bezier(0.22, 1, 0.36, 1);
      }
      ${[1, 2, 3, 4].map(
        (i) => `
        > div:nth-of-type(${i}) > * {
          transition-delay: ${50 + 50 * i}ms;
        }
      `
      )}
    }
  }
  &.exit {
    opacity: 1;
    transform: translateY(0);
  }
  &.exit-active {
    opacity: 0;
    transform: translateY(-80px);
  }
`

const dropdownContainerStyle = (
  startingYPosition: number,
  scrolled: boolean,
  customBreakPoint: number,
  isDark: boolean
) => css`
  position: fixed;
  top: ${scrolled ? HEADER_HEIGHT : startingYPosition + HEADER_HEIGHT}px;
  // z-index: 7 (>= 6) is important!
  // Otherwise the Header is overlayed by the SearchBar (z-index: 6)
  // or by the SearchResultsBlock (z-index: 5)
  // or by the LoadingSpinner (z-index: 4)
  z-index: 7;
  box-shadow: 0 30px 60px -30px rgb(0 0 0 / 14.9%);
  background-color: ${isDark ? '#071322' : '#fff'};
  left: 0;

  .strong-body-2 {
    margin-bottom: 0.125rem;
  }

  ${isDark &&
  `
    @media screen and (min-width: ${customBreakPoint + 1}px) {
      border-bottom: 1px solid rgba(255, 255, 255, 0.2);
    }
  `};

  @media screen and (max-width: ${customBreakPoint}px) {
    width: calc(100vw - 4rem);
  }

  @media screen and (max-width: ${breakpoints.small}px) {
    width: calc(100vw - 3rem);
  }
`

export const emojiStyle = css`
  display: inline-block;
  margin-right: 5px;
`

const distanceToTop = (elem: HTMLElement | null) => {
  if (elem) {
    const distance = elem.getBoundingClientRect().top
    return distance
  }
  return 0
}

const chevronStyle = (isDark: boolean, isDropDownOpen: boolean, customBreakPoint?: number) => css`
  margin-left: 4px;
  ${isDark && ' path { stroke: #fff; }'};
  transform: translateY(-2px) rotate(${isDropDownOpen ? '180deg' : '0'});
  transition: transform 0.3s ease;

  ${customBreakPoint &&
  customBreakPoint < headerBreakPoint &&
  `
    @media screen and (min-width: ${customBreakPoint + 1}px) and (max-width: ${headerBreakPoint}px) {
      transform: translateY(4px) rotate(${isDropDownOpen ? '180deg' : '0'}) !important;
      width: 0.75rem !important;
      }
      `}

  @media screen and (max-width: ${customBreakPoint}px) {
    transform: translateY(0) rotate(${isDropDownOpen ? '180deg' : '0'});
    width: 1rem;
    height: 1rem;
  }
`
